import React, { useState, useMemo, useEffect } from "react";
import { Button, Dimmer, Dropdown, Input, Loader } from "semantic-ui-react";
import { formatComma } from "react-lib/utils/tsUtils";

// UX
import CardPayingUX from "./CardPayingUX";
import SubRepeaterPayMethodUX from "./SubRepeaterPayMethodUX";
import CardPayingMeBillingUX from "react-lib/apps/MEH/CardPayingMeBillingUX";

// Common
import ModConfirm from "react-lib/apps/common/cnmi/ModConfirm";
import ModInfo from "react-lib/apps/common/ModInfo";

// ---
import { CARD_MEDICAL_FEE } from "./CardMedicalFee";

// Utils
import CONFIG from "config/config";
import { useIntl } from "react-intl";

// Styles
import "./CardPaying.scss";

type CardPayingProps = {
  onEvent: (e: any) => any;
  setProp: (key: string, value: any, callback?: Function) => any;
  // seq
  BillPaymentSequence?: Record<string, any> | null;
  // data
  patientInfo?: Record<string, any>;
  citizenDetail: {
    text: string;
    patientId: string;
  };
  defaultAmount?: Partial<{
    price: number;
    reimbursable: number;
    cashonly: number;
    discount: number;
    payable: number;
  }> | null;
  patientOptionData: {
    options: {
      key: string;
      value: string;
      text: any;
    }[];
    value: string;
  };
  invoiceData: {
    items: any[];
    ids: any[];
    orderBy?: string;
  };
  invoiceItemByItems?: any[];
  // CommonInterface
  errorMessage?: Record<string, any>;
  // BillingHistory
  invoiceId?: number;
  showFilter?: boolean;
  // #historyInvoiceData?: any;
  // config
  openPaying?: boolean;
  simpleReceipt?: boolean;
  isMeBilling?: boolean;
  // callback
  onSuccess?: () => any;
  isDeposit?: boolean;
  walletType?: any;
  bilReceiptCodeDetail?: any;
  masterOptions?: Record<string, any[]>;
  languageUX?: string;
};

type ModType = {
  receiptCode: string;
  message: any;
  status: "success" | "confirm" | "error_payment";
};

const DOCTOR_CODE_REGEX = /\s*\([^)]+\)$/;

const CardPaying = (props: CardPayingProps) => {
  const intl = useIntl();
  // Mod
  const [openModInfo, setOpenModInfo] = useState<ModType | null>(null);
  // Loading
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //
  const [sumAmount, setSumAmount] = useState();
  const [defaultAmount, setDefaultAmount] = useState();
  const [changeText, setChangeText] = useState("0.00");
  const [receiptMode, setReceiptMode] = useState<"auto" | "manual">("auto");
  const [receiptNo, setReceiptNo] = useState("");
  const [patientOptions, setPatientOptions] = useState(props.patientOptionData?.options || []);

  const formatPaymentText = (data: any) => {
    return formatComma(Number(data).toFixed(2).replaceAll(",", ""));
  };

  // Use Effect
  useEffect(() => {
    if (props.BillPaymentSequence?.sumAmount) {
      setIsLoading(false);

      let sumAmountData: any;

      (Object.keys(props.BillPaymentSequence?.sumAmount) || []).forEach((item: any) => {
        sumAmountData = {
          ...sumAmountData,
          [item]: formatPaymentText(props.BillPaymentSequence?.sumAmount?.[item]),
        };
      });

      setSumAmount(sumAmountData);
    } else {
      setIsLoading(true);
    }
  }, [props.BillPaymentSequence?.sumAmount]);

  useEffect(() => {
    if (props.defaultAmount) {
      setIsLoading(false);

      let defaultAmountData: any;

      (Object.keys(props.defaultAmount) || []).forEach((item: any) => {
        defaultAmountData = {
          ...defaultAmountData,
          [item]: formatPaymentText(props.defaultAmount?.[item]),
        };
      });

      setDefaultAmount(defaultAmountData);
    }
  }, [props.defaultAmount]);

  useEffect(() => {
    const rawOweText = Number(props.BillPaymentSequence?.rawOweText) || 0;
    const rawTotalText = Number(props.BillPaymentSequence?.rawTotalText) || 0;
    const invoiceDiscountsText = Number(props.BillPaymentSequence?.invoiceDiscountsText) || 0;

    const newRawOweText = rawOweText - invoiceDiscountsText;

    setChangeText(
      newRawOweText > rawTotalText ? "0.00" : formatPaymentText(rawTotalText - newRawOweText),
    );
  }, [
    props.BillPaymentSequence?.rawOweText,
    props.BillPaymentSequence?.rawTotalText,
    props.BillPaymentSequence?.invoiceDiscountsText,
  ]);

  useEffect(() => {
    if (!!props.errorMessage?.[CARD_MEDICAL_FEE] && props.openPaying) {
      setIsLoading(false);
    }
  }, [props.openPaying, props.errorMessage]);

  useEffect(() => {
    setPatientOptions(props.patientOptionData?.options || []);
  }, [props.patientOptionData?.options]);

  //  Use Memo
  const masterAccountOptions = useMemo(() => {
    return (props.BillPaymentSequence?.masterAccountList || []).map((item: any) => ({
      key: item.id,
      text: item.name,
      value: item.id,
    }));
  }, [props.BillPaymentSequence?.masterAccountList]);

  const payments = useMemo(() => {
    return (props.BillPaymentSequence?.paymentMethodList || []).filter(
      (item: any) => item.type !== "1" && item.type !== "6",
    );
  }, [props.BillPaymentSequence?.paymentMethodList]);

  const masterEDCOptions = useMemo(() => {
    return (props.BillPaymentSequence?.masterEDCList || []).map((item: any) => ({
      key: item.id,
      text: item.name,
      value: item.id,
    }));
  }, [props.BillPaymentSequence?.masterEDCList]);

  const depositTotal = useMemo(() => {
    const total = props.BillPaymentSequence?.depositTotal || "0";

    const totalString: string = (total || "0").replaceAll(",", "");
    const cashDeposit = props.BillPaymentSequence?.cashDepositText || "0";

    const result = (parseFloat(totalString) - parseFloat(cashDeposit)).toFixed(2);

    return result
      ? Number(result).toLocaleString("en-US", {
          style: "decimal",
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        })
      : "";
  }, [props.BillPaymentSequence?.depositTotal, props.BillPaymentSequence?.cashDepositText]);

  const doctorNameOptions = useMemo(() => {
    return (props.masterOptions?.doctor || []).map((item: any) => {
      const doctorName = (item.text || "").replace(DOCTOR_CODE_REGEX, "");

      return {
        key: item.key,
        value: item.value,
        text: doctorName,
      };
    });
  }, [props.masterOptions?.doctor]);

  const patientNameAndHN = useMemo(() => {
    const nameCode: string = props.patientInfo?.name_code || "";
    const hn: string = props.patientInfo?.hn || "";

    if (!nameCode || !hn.includes("_")) {
      return nameCode;
    }

    return nameCode.replace(hn, props.patientInfo?.division_hn || hn.split("_").pop() || hn);
  }, [props.patientInfo?.division_hn, props.patientInfo?.hn, props.patientInfo?.name_code]);

  const handlePay = (data: any) => {
    props.onEvent({
      message: "RunSequence",
      params: {
        sequence: "BillPayment",
        card: CARD_MEDICAL_FEE,
        action: "pay",
        patientNameLang: props.patientOptionData?.value,
        callback: setOpenModInfo,
        loading: setIsLoading,
        invoice: props.invoiceId ? props.invoiceId : null,
        invoiceData: props.invoiceData,
        confirmData: data.confirmData,
        simpleReceipt: props.simpleReceipt,
        isDeposit: props.isDeposit,
        walletType: props.walletType,
        showFilter: props.showFilter,
        invoiceItemByItems: props.invoiceItemByItems,
        isPayment: true,
        isMeBilling: props.isMeBilling,
        ...(receiptMode === "manual" && receiptNo && { receiptNo: receiptNo }),
      },
    });
  };

  const handleAddPaymentMethod = (type: number) => {
    props.onEvent({
      message: "HandleSetPaymentMethodList",
      params: {
        list: [
          ...(props.BillPaymentSequence?.paymentMethodList || []),
          { type, paymentNo: "", paymentTarget: 0, value: 0 },
        ],
      },
    });
  };

  const handleChangeInput = (e: any, v: any) => {
    props.onEvent({
      message: "HandleChangePaymentMethodList",
      params: { e, v },
    });
  };

  const handleKeyDownCouponNumber = (e: any, v: any) => {
    props.onEvent({
      message: "HandleKeyDownCouponNumber",
      params: { e, v, idList: props.invoiceData.ids },
    });
  };

  const handleRemovePaymentMethod = (index: number) => {
    const filter = props.BillPaymentSequence?.paymentMethodList.filter(
      (_: any, idx: number) => idx !== index,
    );
    props.onEvent({
      message: "HandleSetPaymentMethodList",
      params: { list: filter },
    });
  };

  const handleCloseModSuccess = async () => {
    setIsLoading(false);
    setOpenModInfo(null);

    await props.setProp("BillPaymentSequence", null);

    props.onSuccess?.();
  };

  const handleChangeAutoEDC = (_e: any, data: any) => {
    let value = typeof data.checked === "boolean" ? data.checked : data.value;
    const detail = props.BillPaymentSequence?.autoEDC || {};

    if (data.name === "check" && value) {
      detail.traceId = "";
      detail.approvalCode = "";
    }

    detail[data.name] = value;

    props.setProp("BillPaymentSequence.autoEDC", { ...detail });
  };

  const handleCloseModInfo = () => {
    setOpenModInfo(null);
  };

  const handleConfirm = () => {
    handlePay({ confirmData: openModInfo?.message });
  };

  const handleKeyDownNumber = (e: any) => {
    if (!/^\d$/.test(e.key) && e.key !== "Backspace") {
      e.preventDefault();
    }
  };

  const handleEnterCashAmountText = (e: any) => {
    if (e.key === "Enter")
      props.onEvent({
        message: "RunSequence",
        params: { sequence: "BillPayment" },
      });
  };

  const handleEnterCashDepositText = async (e: any) => {
    if (e.key === "Enter") {
      handleBlurCashDepositText(e);
    }
  };

  const handleEnterInvoiceDiscountsText = (e: any) => {
    if (e.key === "Enter")
      props.onEvent({
        message: "RunSequence",
        params: { sequence: "BillPayment" },
      });
  };

  const handleBlurCashAmountText = (e: any) => {
    props.onEvent({
      message: "RunSequence",
      params: { sequence: "BillPayment", isUpdateCashAmount: true },
    });
  };

  const handleBlurCashDepositText = async (e: any) => {
    const total: string = (props.BillPaymentSequence?.depositTotal || "0").replaceAll(",", "");

    const sumTotal =
      Number(props.BillPaymentSequence?.cashAmountText || 0) +
      Number(props?.BillPaymentSequence?.transferText || 0) +
      Number(props.BillPaymentSequence?.cardText || 0) +
      Number(props?.BillPaymentSequence?.chequeText || 0) +
      Number(props.BillPaymentSequence?.couponText || 0) +
      Number(props.BillPaymentSequence?.installmentText || 0) +
      Number(props.BillPaymentSequence?.cardDebitText || 0);

    let cashDeposit = props.BillPaymentSequence?.cashDepositText;
    let cashOwe = Number(props.BillPaymentSequence?.sumAmount?.payable) - Number(sumTotal);
    let maxCash = Number(total) > cashOwe ? cashOwe : Number(total);

    if (Number(cashDeposit) > Number(total)) {
      alert("ยอดเงินเกินยอดคงเหลือเงินมัดจำ");
      await props.setProp("BillPaymentSequence.cashDepositText", maxCash);
    } else if (Number(cashDeposit) > Number(props.BillPaymentSequence?.sumAmount?.payable)) {
      alert("ยอดเกินจำนวนค้างชำระ");
      await props.setProp("BillPaymentSequence.cashDepositText", maxCash);
    }

    props.onEvent({
      message: "RunSequence",
      params: { sequence: "BillPayment", isUpdateCashDeposit: true },
    });
  };

  const handleBlurInvoiceDiscountsText = (e: any) => {
    props.onEvent({
      message: "RunSequence",
      params: { sequence: "BillPayment", isUpdateInvoiceDiscounts: true },
    });
  };

  const handleChangeDoctorName = (e: any, data: any) => {
    props.setProp("BillPaymentSequence.doctorName", data.value);
  };

  const PayingComponent = props.isMeBilling ? CardPayingMeBillingUX : CardPayingUX;

  return (
    <>
      <PayingComponent
        // data
        isDeposit={props.isDeposit}
        isCudent={CONFIG.BIL_RECEIPT === "cudent"}
        // config
        isMeBilling={props.isMeBilling}
        // #68165
        // isDeposit={true}
        patientNameAndHN={patientNameAndHN}
        citizenID={props.citizenDetail.text}
        sumAmount={sumAmount || defaultAmount || {}}
        cashDepositText={props.BillPaymentSequence?.cashDepositText || ""}
        cashAmountText={props.BillPaymentSequence?.cashAmountText || ""}
        transferText={formatPaymentText(props?.BillPaymentSequence?.transferText || "")}
        cardText={formatPaymentText(props.BillPaymentSequence?.cardText || "")}
        chequeText={formatPaymentText(props.BillPaymentSequence?.chequeText || "")}
        couponText={formatPaymentText(props.BillPaymentSequence?.couponText || "")}
        installmentText={formatPaymentText(props.BillPaymentSequence?.installmentText || "")}
        cardDebitText={formatPaymentText(props.BillPaymentSequence?.cardDebitText || "")}
        totalText={formatPaymentText(props.BillPaymentSequence?.totalText || "")}
        changeText={changeText}
        rawTotalText={props.BillPaymentSequence?.rawTotalText || ""}
        oweText={formatPaymentText(props.BillPaymentSequence?.oweText || "")}
        rawOweText={props.BillPaymentSequence?.rawOweText || ""}
        roundOffText={formatPaymentText(props.BillPaymentSequence?.roundOffText || "")}
        remarkText={props.BillPaymentSequence?.remarkText || ""}
        // AutoEDC
        check={props.BillPaymentSequence?.autoEDC?.check}
        approvalType={props.BillPaymentSequence?.autoEDC?.approvalType}
        traceId={props.BillPaymentSequence?.autoEDC?.traceId}
        approvalCode={props.BillPaymentSequence?.autoEDC?.approvalCode}
        showAutoEDC={props.BillPaymentSequence?.requireApprovalCodeCheck?.require_approval_code}
        doctorName={props.BillPaymentSequence?.doctorName || ""}
        // callback
        onChangeCashAmountText={(e: any) => {
          if (isNaN(e.target.value) || parseFloat(e.target.value) < 0) {
            alert("กรุณากรอกตัวเลข");
          } else {
            props.setProp("BillPaymentSequence.cashAmountText", e.target.value);
          }
        }}
        depositTotal={depositTotal}
        onChangeCashDepositText={(e: any) => {
          if (isNaN(e.target.value) || parseFloat(e.target.value) < 0) {
            alert("กรุณากรอกตัวเลข");
          } else {
            props.setProp("BillPaymentSequence.cashDepositText", e.target.value);
          }
        }}
        onChangeAutoEDC={handleChangeAutoEDC}
        onEnterCashAmountText={handleEnterCashAmountText}
        onEnterCashDepositText={handleEnterCashDepositText}
        onBlurCashAmountText={handleBlurCashAmountText}
        onBlurCashDepositText={handleBlurCashDepositText}
        cannotPay={props?.BillPaymentSequence?.cannotPay}
        onPay={handlePay}
        onAddPaymentMethod={handleAddPaymentMethod}
        onChangeRemark={(e: any, data: any) => {
          props.setProp("BillPaymentSequence.remarkText", data.value);
        }}
        // Element
        Loader={
          <Dimmer active={isLoading && openModInfo?.status !== "confirm"} inverted>
            <Loader />
          </Dimmer>
        }
        // ErrorMessage={
        // <ErrorMessage error={props.BillPaymentSequence?.payErrorMessage} />
        // }
        SubRepeaterPayMethod={
          <>
            {payments.map((item: any, index: number) => (
              <SubRepeaterPayMethodUX
                key={"sub-repeater-paymethod" + item.id}
                type={item.type}
                paymentNo={item.paymentNo}
                paymentTarget={item.paymentTarget}
                value={item.value}
                paymentDate={item.paymentDate}
                couponMessage={item.couponMessage}
                masterAccountOptions={masterAccountOptions}
                masterEDCOptions={masterEDCOptions}
                onChange={(e: any, v: any) => {
                  if (v.name === "value" && (isNaN(v.value) || parseFloat(v.value) < 0)) {
                    alert("กรุณากรอกตัวเลข");
                  } else {
                    handleChangeInput(e, { ...v, index: index + 2 });
                  }
                }}
                onChangeDate={(date: string) => {
                  props.setProp(
                    `BillPaymentSequence.paymentMethodList.${index + 2}.paymentDate`,
                    date,
                  );
                }}
                onKeyDownCouponNumber={(e: any, v: any) =>
                  handleKeyDownCouponNumber(e, { ...v, index: index + 2 })
                }
                onClose={() => handleRemovePaymentMethod(index + 2)}
                handleKeyDownNumber={handleKeyDownNumber}
                languageUX={props.languageUX}
              />
            ))}
          </>
        }
        doctorDropDown={
          <Dropdown
            fluid
            search
            upward
            selection
            onChange={handleChangeDoctorName}
            options={doctorNameOptions}
            style={{ marginRight: "1rem" }}
            value={props.BillPaymentSequence?.doctorName || ""}
          ></Dropdown>
        }
        patientDropDown={
          <Dropdown
            fluid
            search
            upward
            selection
            allowAdditions
            onChange={(e: any, v: any) => {
              props.setProp("billingPatientLanguage.value", v.value);
            }}
            onAddItem={(e: any, { value }: any) => {
              setPatientOptions((prev) => [
                ...prev,
                {
                  key: value,
                  text: value,
                  value: value,
                },
              ]);

              props.setProp("billingPatientLanguage.value", value);
            }}
            options={patientOptions}
            style={{ marginRight: "1rem" }}
            value={props.patientOptionData?.value}
          ></Dropdown>
        }
        receiptCode={
          props.isMeBilling ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div>
                <label>เลขที่ใบเสร็จ :</label>
              </div>
              <div className="receipt-mode">
                <Button
                  size="mini"
                  active={receiptMode === "auto"}
                  onClick={() => {
                    setReceiptMode("auto");
                    setReceiptNo("");
                  }}
                >
                  AUTO
                </Button>

                <Button
                  size="mini"
                  active={receiptMode === "manual"}
                  onClick={() => {
                    setReceiptMode("manual");
                    setReceiptNo("");
                  }}
                >
                  กำหนดเอง
                </Button>
              </div>
              <div>
                {receiptMode === "auto" ? (
                  <div>
                    {props.bilReceiptCodeDetail?.code === undefined
                      ? "-"
                      : props.bilReceiptCodeDetail.code || "ยังไม่ได้ตั้งเลขที่ใบเสร็จ"}
                  </div>
                ) : (
                  <Input
                    size="small"
                    placeholder="กรอกเลขที่ใบเสร็จ"
                    value={receiptNo}
                    onChange={(e) => setReceiptNo(e.target.value)}
                  />
                )}
              </div>
            </div>
          ) : (
            <div>
              `เลขที่ใบเสร็จ : $
              {props.bilReceiptCodeDetail?.code === undefined
                ? "-"
                : props.bilReceiptCodeDetail.code || "ยังไม่ได้ตั้งเลขที่ใบเสร็จ"}
              `
            </div>
          )
        }
        inputCashAmountText={
          <input
            onBlur={handleBlurCashAmountText}
            onChange={(e: any) => {
              if (isNaN(e.target.value) || parseFloat(e.target.value) < 0) {
                alert("กรุณากรอกตัวเลข");
              } else {
                props.setProp("BillPaymentSequence.cashAmountText", e.target.value);
              }
            }}
            onKeyUp={handleEnterCashAmountText}
            style={{ textAlign: "right" }}
            value={props.BillPaymentSequence?.cashAmountText || ""}
            placeholder="กรอกจำนวนเงิน & กด Enter"
          />
        }
        inputDiscountCode={
          <input
            onBlur={handleBlurInvoiceDiscountsText}
            onChange={(e: any) => {
              if (isNaN(e.target.value) || parseFloat(e.target.value) < 0) {
                alert("กรุณากรอกตัวเลข");
              } else {
                props.setProp("BillPaymentSequence.invoiceDiscountsText", e.target.value);
              }
            }}
            onKeyUp={handleEnterInvoiceDiscountsText}
            style={{ textAlign: "right", border: "1px solid rgba(243, 130, 160, 1)" }}
            value={props.BillPaymentSequence?.invoiceDiscountsText || ""}
            placeholder="กรอกส่วนลดรวม & กด Enter"
          />
        }
        languageUX={props.languageUX}
      />

      {openModInfo?.status === "success" && (
        <ModInfo
          open={true}
          titleColor={"green"}
          titleName={intl.formatMessage({ id: "บันทึกสำเร็จ" })}
          alertText={
            <div style={{ fontSize: "15px" }}>
              <div style={{ fontWeight: "bold", display: "flex", padding: "5px 0" }}>
                รับชำระเงินผู้ป่วยสำเร็จ ใบเสร็จเลขที่ :{" "}
                <span style={{ color: "#2185d0" }}>{openModInfo?.receiptCode || "-"}</span>
              </div>
              {openModInfo.message.length > 0 && (
                <div>
                  <div style={{ fontWeight: "bold", display: "flex", padding: "15px 0px" }}>
                    รายการคำสั่งที่ผู้ป่วยยังไม่ได้บริการ
                  </div>
                  <div>
                    {(openModInfo?.message || [])?.map((item: any) => {
                      return (
                        <div style={{ display: "flex", justifyContent: "flex-start" }}>
                          <li>{item}</li>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          }
          onApprove={handleCloseModSuccess}
        />
      )}

      <ModInfo
        open={openModInfo?.status === "error_payment"}
        titleColor={"red"}
        titleName={intl.formatMessage({ id: "แจ้งเตือน!! ไม่สามารถรับชำระเงินได้" })}
        alertText={
          <div style={{ padding: "5px 0" }}>
            <p style={{ textAlign: "center", marginBottom: "0.5rem" }}>
              <strong>
                การยืนยันสิทธิไม่สำเร็จ
                <br />
                เนื่องจากไม่พบรหัส Approval Code
                <br />
                กรุณาตรวจสอบอีกครั้ง
                <br />
              </strong>
            </p>
            <b>
              <p style={{ color: "red" }}>
                Approval Code: -<br />
                ยอดชำระ: {props.BillPaymentSequence?.edcTransactionAmount?.total_amount} บาท
              </p>
            </b>
          </div>
        }
        onApprove={handleCloseModInfo}
      />

      <ModConfirm
        openModal={openModInfo?.status === "confirm"}
        titleName={intl.formatMessage({ id: "ยืนยันการชำระเงิน!" })}
        titleColor="blue"
        size="mini"
        denyButtonColor="red"
        denyButtonText={intl.formatMessage({ id: "ยกเลิก" })}
        approveButtonColor="green"
        approveButtonText={intl.formatMessage({ id: "ยืนยัน" })}
        content={
          <div
            style={{
              fontWeight: "bold",
              textAlign: "center",
              margin: "-0.75rem 0px -1.5rem",
            }}
          >
            <Dimmer active={isLoading} inverted>
              <Loader />
            </Dimmer>
            <p style={{ textAlign: "center", marginBottom: "0.5rem" }}>
              <strong>
                กรุณาตรวจสอบ Approval Code และยอดชำระอีกครั้งก่อนกดยืนยัน
                <br />
              </strong>
            </p>
            <b>
              <p style={{ color: "darkblue" }}>
                Approval Code: {openModInfo?.message?.approvalCode}
                <br />
                ยอดชำระ: {props.BillPaymentSequence?.edcTransactionAmount?.total_amount} บาท
              </p>
            </b>
          </div>
        }
        onApprove={handleConfirm}
        onDeny={handleCloseModInfo}
        onCloseWithDimmerClick={handleCloseModInfo}
      />
    </>
  );
};

CardPaying.displayName = "CardPaying";

export default React.memo(CardPaying);
